// Cross-host SSH relay dialer.
//
// Opens an E2EE relay channel from THIS daemon (A) to a SIBLING daemon (B),
// telling B to do a `net.connect(127.0.0.1:22)` and pump bytes between the TCP
// socket and the channel. The result is a duplex byte stream that the local
// CLI proxy hands to `ssh` via ProxyCommand.
//
// What this module does:
//   1. Fetches B's offer (`serverId`, `daemonPubkey`, `relayEndpoint`) from
//      the auth-server via `GET /daemon/peers/:serverId/offer` (daemon-Bearer
//      authed; gated by the shared `canUserAccessHost` predicate).
//   2. Mints a short-lived wsToken via `POST /v2/ws-token` (also daemon-Bearer
//      authed thanks to Phase 1c) bound to (clientIdentityPubkey, B.serverId).
//   3. Opens a CLIENT-role WebSocket to the relay against B's `serverId` — the
//      relay DO pairs this socket to B's existing server-control socket
//      exactly as it does for app clients (no relay-protocol changes).
//   4. Runs `createClientChannel` against B's `daemonPubkey` with this daemon's
//      persistent client-identity keypair (so B's allow-list resolves us to
//      our owner userId via the devices table).
//   5. Sends a hello frame with `mode: "tcp-forward"`, `wsToken`, and the
//      loopback target `127.0.0.1:22`.
//   6. Exposes the open channel as `{ sendBinary(chunk), onData(handler),
//      close() }` — a duplex byte stream. The byte pump on top of this is
//      shared with the accept path (see `byte-pump.ts`).
//
// Backpressure: relies on the encrypted channel's MAX_PENDING_SENDS = 200
// queue + chunked 64 KB writes from the caller. We don't add ack frames.

import { createHash } from "node:crypto";
import fs from "node:fs";
import fsp from "node:fs/promises";
import os from "node:os";
import nodePath from "node:path";

import { WebSocket } from "ws";
import type { Logger } from "pino";

import {
  createClientChannel,
  type EncryptedChannel,
  type KeyPair,
  type Transport as RelayTransport,
} from "@appostle/relay/e2ee";
import { buildDaemonWebSocketUrl, buildRelayWebSocketUrl } from "../../shared/daemon-endpoints.js";
import { hostExecFrameSchema } from "./host-exec-protocol.js";
import {
  HOST_TRANSFER_ACK_EVERY,
  HOST_TRANSFER_CHUNK_BYTES,
  HOST_TRANSFER_IDLE_TIMEOUT_MS,
  HOST_TRANSFER_MAX_DURATION_MS,
  HOST_TRANSFER_MAX_FILE_BYTES,
  HOST_TRANSFER_WINDOW_CHUNKS,
  type HostTransferFrame,
  hostTransferFrameSchema,
} from "./host-transfer-protocol.js";

// Mirror the (currently file-local) WS_PROTOCOL_VERSION constant from
// websocket-server.ts. Keep in sync — both ends must agree.
const WS_PROTOCOL_VERSION = 1;

// --- Auth-server lookups ----------------------------------------------------

interface PeerOffer {
  serverId: string;
  daemonPubkey: string;
  relayEndpoint: string;
  directEndpoints?: DirectEndpointCandidate[];
}

export interface DirectEndpointCandidate {
  endpoint: string;
  source: string;
  observedAt: string;
}

interface MintedWsToken {
  token: string;
  expiresAt: string;
}

/** A sibling host as returned by `GET /daemon/peers` (no daemonPubkey/endpoint). */
export interface PeerSummary {
  serverId: string;
  name: string;
  icon?: string | null;
  online: boolean;
  directEndpoints?: DirectEndpointCandidate[];
  organization?: { id: string; name: string; slug: string | null; logo?: string | null } | null;
  owner?: { userId: string; handle: string | null; image?: string | null } | null;
}

export interface AuthServerLookup {
  fetchPeerOffer(siblingServerId: string): Promise<PeerOffer>;
  mintWsToken(args: { device: string; hostServerId: string }): Promise<MintedWsToken>;
  /** List sibling hosts this daemon's owner can access (minus self). */
  listPeers(): Promise<PeerSummary[]>;
}

/**
 * Build the auth-server lookup helper bound to this daemon's `authServerUrl` +
 * `daemonToken`. Both new endpoints (`/daemon/peers/:serverId/offer` and
 * daemon-authed `/v2/ws-token`) are gated by the same access predicate, so a
 * 404/403 here means the sibling host is genuinely out of reach for this
 * daemon's owner.
 */
export function createAuthServerLookup(options: {
  logger: Logger;
  authServerUrl: string;
  daemonToken: string;
}): AuthServerLookup {
  const { authServerUrl, daemonToken } = options;
  const log = options.logger.child({ component: "cross-host-auth-lookup" });

  async function request<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), 10_000);
    try {
      const url = new URL(path, authServerUrl).toString();
      const res = await fetch(url, {
        method,
        headers: {
          authorization: `Bearer ${daemonToken}`,
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: ac.signal,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "(unreadable)");
        throw new Error(`auth-server ${method} ${path} failed: ${res.status} ${text}`);
      }
      return (await res.json()) as T;
    } catch (err) {
      log.warn({ err, method, path }, "cross-host auth-server request failed");
      throw err;
    } finally {
      clearTimeout(t);
    }
  }

  return {
    fetchPeerOffer(siblingServerId) {
      return request<PeerOffer>(
        "GET",
        `/daemon/peers/${encodeURIComponent(siblingServerId)}/offer`,
      );
    },
    mintWsToken({ device, hostServerId }) {
      return request<MintedWsToken>("POST", "/v2/ws-token", {
        device,
        hostServerId,
      });
    },
    async listPeers() {
      const { peers } = await request<{ peers: PeerSummary[] }>("GET", "/daemon/peers");
      return peers;
    },
  };
}

// --- Dialer -----------------------------------------------------------------

/**
 * Duplex byte stream over the opened E2EE relay channel. The dialer hands this
 * to its caller (the CLI proxy / byte pump) after the tcp-forward hello has
 * been sent.
 */
export interface TcpForwardChannel {
  /** Send a chunk of TCP bytes towards the remote `127.0.0.1:22`. */
  sendBinary(chunk: ArrayBuffer): Promise<void>;
  /** Register a handler for inbound TCP bytes (or a JSON status frame). */
  onData(handler: (data: string | ArrayBuffer) => void): void;
  onClose(handler: (code: number, reason: string) => void): void;
  onError(handler: (error: unknown) => void): void;
  /** Close the channel + underlying WS. */
  close(code?: number, reason?: string): void;
}

const TCP_FORWARD_HELLO_TIMEOUT_MS = 15_000;
const DIRECT_ENDPOINT_MAX_AGE_MS = 2 * 60_000;
const DIRECT_DIAL_TIMEOUT_MS = 2_500;

export type CrossHostTransportKind = "direct" | "relay";

/**
 * Copy a Node Buffer into a fresh ArrayBuffer. Node's `buffer.buffer` view can
 * be larger than the buffer itself (shared underlying allocation) AND its type
 * is `ArrayBufferLike` (covers `SharedArrayBuffer`), so we can't pass it
 * directly where `ArrayBuffer` is required. The copy is small (TCP chunks),
 * negligible vs the encryption pass that follows.
 */
function bufferToArrayBuffer(buf: Buffer): ArrayBuffer {
  const out = new ArrayBuffer(buf.byteLength);
  new Uint8Array(out).set(buf);
  return out;
}

function freshDirectEndpoints(peer: PeerOffer): DirectEndpointCandidate[] {
  const now = Date.now();
  const seen = new Set<string>();
  return (peer.directEndpoints ?? []).flatMap((candidate) => {
    // `lan-interface:*` candidates are SSH-probe-only hints. The sibling's
    // daemon does NOT listen on that address — its HTTP listener is on
    // loopback. Skip here so we don't burn a 2.5s dial timeout per LAN IP.
    if (candidate.source.startsWith("lan-interface:")) return [];
    const observedAt = Date.parse(candidate.observedAt);
    if (!Number.isFinite(observedAt) || now - observedAt > DIRECT_ENDPOINT_MAX_AGE_MS) {
      return [];
    }
    if (seen.has(candidate.endpoint)) return [];
    seen.add(candidate.endpoint);
    return [candidate];
  });
}

function relayUrlForPeer(peer: PeerOffer): string {
  return buildRelayWebSocketUrl({
    endpoint: peer.relayEndpoint,
    serverId: peer.serverId,
    role: "client",
    version: 2,
  });
}

function candidateUrlsForPeer(peer: PeerOffer): Array<{
  kind: CrossHostTransportKind;
  wsUrl: string;
  endpoint: string;
}> {
  return [
    ...freshDirectEndpoints(peer).map((candidate) => ({
      kind: "direct" as const,
      wsUrl: buildDaemonWebSocketUrl(candidate.endpoint),
      endpoint: candidate.endpoint,
    })),
    { kind: "relay" as const, wsUrl: relayUrlForPeer(peer), endpoint: peer.relayEndpoint },
  ];
}

async function openWebSocketTransport(args: {
  log: Logger;
  wsUrl: string;
  kind: CrossHostTransportKind;
}): Promise<{ socket: WebSocket; transport: RelayTransport }> {
  const timeoutMs = args.kind === "direct" ? DIRECT_DIAL_TIMEOUT_MS : 10_000;
  args.log.debug({ wsUrl: args.wsUrl, transport: args.kind }, "dialing cross-host websocket");
  const socket = new WebSocket(args.wsUrl, {
    handshakeTimeout: timeoutMs,
    perMessageDeflate: false,
  });
  socket.binaryType = "arraybuffer";

  const transport: RelayTransport = {
    send: (data) => {
      if (typeof data === "string") {
        socket.send(data);
      } else if (data instanceof ArrayBuffer) {
        socket.send(Buffer.from(data));
      } else {
        socket.send(String(data));
      }
    },
    close: (code, reason) => {
      try {
        socket.close(code, reason);
      } catch {
        // ignore
      }
    },
    onmessage: null,
    onclose: null,
    onerror: null,
  };

  socket.on("message", (raw) => {
    if (typeof raw === "string") {
      transport.onmessage?.(raw);
      return;
    }
    if (raw instanceof ArrayBuffer) {
      transport.onmessage?.(raw);
      return;
    }
    if (Buffer.isBuffer(raw)) {
      transport.onmessage?.(bufferToArrayBuffer(raw));
      return;
    }
    const joined = Buffer.concat(raw as Buffer[]);
    transport.onmessage?.(bufferToArrayBuffer(joined));
  });
  socket.on("close", (code, reason) => {
    transport.onclose?.(code, reason.toString("utf8"));
  });
  socket.on("error", (err) => {
    transport.onerror?.(err);
  });

  await new Promise<void>((resolve, reject) => {
    const onOpen = () => {
      socket.off("error", onError);
      resolve();
    };
    const onError = (err: unknown) => {
      socket.off("open", onOpen);
      reject(err);
    };
    socket.once("open", onOpen);
    socket.once("error", onError);
  });

  return { socket, transport };
}

export interface DialTcpForwardArgs {
  logger: Logger;
  /** This daemon's persistent client-identity keypair (NOT the server-role keypair). */
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
  /** Stable per-dialer client identifier — included in the hello like a normal app client. */
  clientId: string;
  /** Resolved sibling offer (caller can supply pre-fetched, or use AuthServerLookup). */
  peer: PeerOffer;
  /** Short-lived wsToken minted for this dial. */
  wsToken: string;
  /** v1: locked to 127.0.0.1:22. Plumbed for the future loopback-port expansion. */
  target?: { host: string; port: number };
}

/**
 * Dial a sibling daemon over the relay and request a `tcp-forward` to its
 * `127.0.0.1:22`. Resolves once the E2EE channel is open and the hello has
 * been sent — the caller MUST start pumping bytes immediately; there is no
 * server-side ACK for the hello in v1 (a JSON status frame may be added
 * later behind a backward-compat envelope).
 */
export async function dialTcpForward(args: DialTcpForwardArgs): Promise<TcpForwardChannel> {
  const { logger, clientIdentity, clientIdentityPubkeyB64, clientId, peer, wsToken } = args;
  const target = args.target ?? { host: "127.0.0.1", port: 22 };
  const log = logger.child({ module: "tcp-forward-dialer", siblingServerId: peer.serverId });

  // 1) Open the raw WS to the relay in CLIENT role against the sibling's serverId.
  const wsUrl = buildRelayWebSocketUrl({
    endpoint: peer.relayEndpoint,
    serverId: peer.serverId,
    role: "client",
    version: 2,
  });

  log.debug({ wsUrl }, "dialing relay (client role)");
  const socket = new WebSocket(wsUrl, {
    handshakeTimeout: 10_000,
    perMessageDeflate: false,
  });
  // Receive Node Buffers as ArrayBuffer so the encrypted-channel byte path is
  // a contiguous ArrayBuffer (binary frames are TCP byte chunks; we don't want
  // a Buffer split across multiple frames).
  socket.binaryType = "arraybuffer";

  // 2) Adapt the WS to the relay Transport shape expected by createClientChannel.
  const transport: RelayTransport = {
    send: (data) => {
      if (typeof data === "string") {
        socket.send(data);
      } else if (data instanceof ArrayBuffer) {
        socket.send(Buffer.from(data));
      } else {
        socket.send(String(data));
      }
    },
    close: (code, reason) => {
      try {
        socket.close(code, reason);
      } catch {
        // ignore
      }
    },
    onmessage: null,
    onclose: null,
    onerror: null,
  };

  // Wire WS → transport callbacks. createClientChannel installs its own
  // onmessage/onclose handlers, so we just bridge raw events into them.
  socket.on("message", (raw) => {
    if (typeof raw === "string") {
      transport.onmessage?.(raw);
      return;
    }
    // `raw` is Buffer | ArrayBuffer | Buffer[] depending on ws version.
    if (raw instanceof ArrayBuffer) {
      transport.onmessage?.(raw);
      return;
    }
    if (Buffer.isBuffer(raw)) {
      transport.onmessage?.(bufferToArrayBuffer(raw));
      return;
    }
    // Buffer[] (fragmented binary). Concatenate then forward.
    const joined = Buffer.concat(raw as Buffer[]);
    transport.onmessage?.(bufferToArrayBuffer(joined));
  });
  socket.on("close", (code, reason) => {
    transport.onclose?.(code, reason.toString("utf8"));
  });
  socket.on("error", (err) => {
    transport.onerror?.(err);
  });

  // Wait for WS to open before kicking off the e2ee handshake.
  await new Promise<void>((resolve, reject) => {
    const onOpen = () => {
      socket.off("error", onError);
      resolve();
    };
    const onError = (err: unknown) => {
      socket.off("open", onOpen);
      reject(err);
    };
    socket.once("open", onOpen);
    socket.once("error", onError);
  });

  // 3) E2EE handshake against the sibling daemon's pubkey.
  const openHandlers = new Set<(d: string | ArrayBuffer) => void>();
  const closeHandlers = new Set<(code: number, reason: string) => void>();
  const errorHandlers = new Set<(e: unknown) => void>();

  let helloSent = false;
  let channelRef: EncryptedChannel | null = null;

  // Resolve with the channel once the hello has been sent. Capturing it on the
  // promise avoids the closure-narrowing problem that hits a mutable outer var.
  const ch = await new Promise<EncryptedChannel>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("tcp-forward hello timeout"));
    }, TCP_FORWARD_HELLO_TIMEOUT_MS);

    createClientChannel(
      transport,
      peer.daemonPubkey,
      {
        onopen: () => {
          // 4) Send the tcp-forward hello immediately on channel-open.
          const c = channelRef;
          if (!c || helloSent) return;
          helloSent = true;
          const hello = {
            type: "hello",
            clientId,
            clientType: "cli",
            protocolVersion: WS_PROTOCOL_VERSION,
            wsToken,
            mode: "tcp-forward",
            target,
          } as const;
          c.send(JSON.stringify(hello))
            .then(() => {
              clearTimeout(timeout);
              resolve(c);
            })
            .catch((err) => {
              clearTimeout(timeout);
              reject(err);
            });
        },
        onmessage: (data) => {
          for (const handler of openHandlers) handler(data);
        },
        onclose: (code, reason) => {
          for (const handler of closeHandlers) handler(code, reason);
        },
        onerror: (err) => {
          for (const handler of errorHandlers) handler(err);
        },
      },
      clientIdentity,
    )
      .then((opened) => {
        channelRef = opened;
      })
      .catch((err) => {
        clearTimeout(timeout);
        reject(err);
      });
  });

  log.debug({ clientIdentityPubkeyB64 }, "tcp-forward channel open");

  return {
    async sendBinary(chunk) {
      await ch.send(chunk);
    },
    onData(handler) {
      openHandlers.add(handler);
    },
    onClose(handler) {
      closeHandlers.add(handler);
    },
    onError(handler) {
      errorHandlers.add(handler);
    },
    close(code = 1000, reason = "Normal closure") {
      try {
        ch.close(code, reason);
      } catch {
        // ignore
      }
    },
  };
}

// --- host-exec dialer -------------------------------------------------------
//
// The non-interactive analogue of `dialTcpForward`. Instead of returning a
// duplex byte stream, it sends the command in the hello, collects the streamed
// stdout/stderr frames the accept side emits, and resolves a single result once
// the `exit` frame arrives. There is NO outbound data leg after the hello.

/** Captured result of a single cross-host command exec. */
export interface HostExecResult {
  stdout: string;
  stderr: string;
  transport: CrossHostTransportKind;
  /** Process exit code (null if killed by signal). */
  code: number | null;
  /** Signal that killed the process, if any. */
  signal: string | null;
  /** A non-output error reported by the accept side (spawn failure, limit hit). */
  error: string | null;
}

export interface DialHostExecArgs {
  logger: Logger;
  /** This daemon's persistent client-identity keypair (NOT the server-role keypair). */
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
  /** Stable per-dialer client identifier — included in the hello like a normal app client. */
  clientId: string;
  /** Resolved sibling offer. */
  peer: PeerOffer;
  /** Short-lived wsToken minted for this dial. */
  wsToken: string;
  /** The command to run on the sibling, plus optional cwd / timeout. */
  exec: { command: string; cwd?: string; timeoutMs?: number };
  /** Live output callback — fired as stdout/stderr frames stream in. */
  onChunk?: (stream: "stdout" | "stderr", text: string) => void;
}

// Safety net on top of the accept side's own timeout (capped at 120s server-
// side). If the channel wedges we still settle the promise.
const HOST_EXEC_DIAL_BUFFER_MS = 20_000;
const HOST_EXEC_DIAL_MAX_MS = 120_000;

/**
 * Dial a sibling daemon over the relay and request a `host-exec` run of
 * `exec.command`. Resolves with the captured `{ stdout, stderr, code, signal,
 * error }` once the sibling streams its `exit` frame (or rejects on transport
 * failure / hello timeout).
 */
export async function dialHostExec(args: DialHostExecArgs): Promise<HostExecResult> {
  const {
    logger,
    clientIdentity,
    clientIdentityPubkeyB64,
    clientId,
    peer,
    wsToken,
    exec,
    onChunk,
  } = args;
  const log = logger.child({ module: "host-exec-dialer", siblingServerId: peer.serverId });

  let opened: Awaited<ReturnType<typeof openWebSocketTransport>> | null = null;
  let selected: ReturnType<typeof candidateUrlsForPeer>[number] | null = null;
  for (const candidate of candidateUrlsForPeer(peer)) {
    try {
      opened = await openWebSocketTransport({
        log,
        wsUrl: candidate.wsUrl,
        kind: candidate.kind,
      });
      selected = candidate;
      break;
    } catch (err) {
      log.warn(
        { err, transport: candidate.kind, endpoint: candidate.endpoint },
        "cross-host websocket dial failed",
      );
      if (candidate.kind === "relay") throw err;
    }
  }
  if (!opened || !selected) {
    throw new Error("no cross-host transport candidate available");
  }
  const { transport } = opened;
  const selectedTransport = selected.kind;

  // 2) E2EE handshake against the sibling daemon's pubkey, then collect frames.
  const dialTimeoutMs =
    Math.min(exec.timeoutMs ?? HOST_EXEC_DIAL_MAX_MS, HOST_EXEC_DIAL_MAX_MS) +
    HOST_EXEC_DIAL_BUFFER_MS;

  const stdoutChunks: Buffer[] = [];
  const stderrChunks: Buffer[] = [];
  let errorMessage: string | null = null;
  let channelRef: EncryptedChannel | null = null;
  let helloSent = false;
  let settled = false;

  return await new Promise<HostExecResult>((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        channelRef?.close(1000, "host_exec_dial_timeout");
      } catch {
        // ignore
      }
      reject(new Error("host-exec dial timeout"));
    }, dialTimeoutMs);

    const finishOk = (code: number | null, signal: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      try {
        channelRef?.close(1000, "host_exec_done");
      } catch {
        // ignore
      }
      resolve({
        stdout: Buffer.concat(stdoutChunks).toString("utf8"),
        stderr: Buffer.concat(stderrChunks).toString("utf8"),
        transport: selectedTransport,
        code,
        signal,
        error: errorMessage,
      });
    };

    const finishErr = (err: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    createClientChannel(
      transport,
      peer.daemonPubkey,
      {
        onopen: () => {
          const c = channelRef;
          if (!c || helloSent) return;
          helloSent = true;
          const hello = {
            type: "hello",
            clientId,
            clientType: "cli",
            protocolVersion: WS_PROTOCOL_VERSION,
            wsToken,
            mode: "host-exec",
            exec,
          } as const;
          c.send(JSON.stringify(hello)).catch(finishErr);
        },
        onmessage: (data) => {
          // host-exec frames always arrive as JSON strings (the accept side
          // sends `ch.send(JSON.stringify(frame))`; the channel UTF-8-decodes
          // valid payloads to a string).
          if (typeof data !== "string") return;
          let parsed: unknown;
          try {
            parsed = JSON.parse(data);
          } catch {
            return;
          }
          const frame = hostExecFrameSchema.safeParse(parsed);
          if (!frame.success) return;
          switch (frame.data.type) {
            case "stdout": {
              const chunk = Buffer.from(frame.data.dataB64, "base64");
              stdoutChunks.push(chunk);
              onChunk?.("stdout", chunk.toString("utf8"));
              break;
            }
            case "stderr": {
              const chunk = Buffer.from(frame.data.dataB64, "base64");
              stderrChunks.push(chunk);
              onChunk?.("stderr", chunk.toString("utf8"));
              break;
            }
            case "error":
              errorMessage = frame.data.message;
              break;
            case "exit":
              finishOk(frame.data.code, frame.data.signal);
              break;
          }
        },
        onclose: (code, reason) => {
          // The accept side closes after the exit frame, so a normal close
          // after we've settled is expected. An early close is a transport
          // failure.
          if (!settled) finishErr(new Error(`channel closed early: ${code} ${reason}`));
        },
        onerror: (err) => {
          finishErr(err);
        },
      },
      clientIdentity,
    )
      .then((opened) => {
        channelRef = opened;
        log.debug({ clientIdentityPubkeyB64 }, "host-exec channel open");
      })
      .catch(finishErr);
  });
}

// --- host-transfer dialer -----------------------------------------------------

export interface DialHostTransferArgs {
  logger: Logger;
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
  clientId: string;
  peer: PeerOffer;
  wsToken: string;
  /** pull: sibling file → local path. push: local file → sibling path. */
  transfer: { direction: "pull" | "push"; remotePath: string; localPath: string };
}

export interface HostTransferResult {
  bytes: number;
  sha256: string;
  transport: CrossHostTransportKind;
}

/**
 * Dial a sibling daemon over the relay and transfer one file in either
 * direction (`mode: "host-transfer"`). Chunked, ack-windowed, sha256-verified
 * end to end. Resolves once the RECEIVING side has verified the digest.
 */
export async function dialHostTransfer(args: DialHostTransferArgs): Promise<HostTransferResult> {
  const { logger, clientIdentity, clientIdentityPubkeyB64, clientId, peer, wsToken, transfer } =
    args;
  const log = logger.child({ module: "host-transfer-dialer", siblingServerId: peer.serverId });

  const localPath = nodePath.resolve(
    transfer.localPath.startsWith("~/")
      ? nodePath.join(os.homedir(), transfer.localPath.slice(2))
      : transfer.localPath,
  );

  // For push, hash + stat the local file up front so the hello announces them.
  let pushSize = 0;
  let pushSha256 = "";
  if (transfer.direction === "push") {
    const stat = await fsp.stat(localPath);
    if (!stat.isFile()) throw new Error(`not a regular file: ${localPath}`);
    if (stat.size > HOST_TRANSFER_MAX_FILE_BYTES) {
      throw new Error(`file is ${stat.size} bytes; cap is ${HOST_TRANSFER_MAX_FILE_BYTES}`);
    }
    pushSize = stat.size;
    pushSha256 = await sha256OfFile(localPath);
  }

  let opened: Awaited<ReturnType<typeof openWebSocketTransport>> | null = null;
  let selected: ReturnType<typeof candidateUrlsForPeer>[number] | null = null;
  for (const candidate of candidateUrlsForPeer(peer)) {
    try {
      opened = await openWebSocketTransport({
        log,
        wsUrl: candidate.wsUrl,
        kind: candidate.kind,
      });
      selected = candidate;
      break;
    } catch (err) {
      log.warn(
        { err, transport: candidate.kind, endpoint: candidate.endpoint },
        "cross-host websocket dial failed",
      );
      if (candidate.kind === "relay") throw err;
    }
  }
  if (!opened || !selected) {
    throw new Error("no cross-host transport candidate available");
  }
  const { transport } = opened;
  const selectedTransport = selected.kind;

  let channelRef: EncryptedChannel | null = null;
  let helloSent = false;
  let settled = false;

  // pull-side receive state.
  let expectedSize = -1;
  let expectedSha = "";
  let nextSeq = 0;
  let bytesWritten = 0;
  const hash = createHash("sha256");
  const partialPath = `${localPath}.appostle-partial`;
  let writeStream: fs.WriteStream | null = null;
  // Serialize async frame handling so chunk writes stay ordered.
  let frameQueue: Promise<void> = Promise.resolve();

  // push-side window state.
  let ackedSeq = -1;
  let onAck: ((seq: number) => void) | null = null;
  let pushReady: (() => void) | null = null;
  let finalOk: ((bytes: number) => void) | null = null;

  return await new Promise<HostTransferResult>((resolve, reject) => {
    let idleTimer: NodeJS.Timeout | null = null;
    const touch = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(
        () => finishErr(new Error("transfer idle timeout")),
        HOST_TRANSFER_IDLE_TIMEOUT_MS,
      );
    };
    const deadline = setTimeout(
      () => finishErr(new Error("transfer exceeded max duration")),
      HOST_TRANSFER_MAX_DURATION_MS,
    );

    const cleanup = () => {
      if (idleTimer) clearTimeout(idleTimer);
      clearTimeout(deadline);
      if (writeStream) {
        writeStream.destroy();
        void fsp.rm(partialPath, { force: true }).catch(() => {});
      }
    };

    const finishOk = (result: HostTransferResult) => {
      if (settled) return;
      settled = true;
      if (idleTimer) clearTimeout(idleTimer);
      clearTimeout(deadline);
      try {
        channelRef?.close(1000, "host_transfer_done");
      } catch {
        // ignore
      }
      resolve(result);
    };
    const finishErr = (err: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      onAck?.(Number.MAX_SAFE_INTEGER);
      try {
        channelRef?.close(1011, "host_transfer_failed");
      } catch {
        // ignore
      }
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    const sendFrame = (c: EncryptedChannel, frame: HostTransferFrame) => {
      c.send(JSON.stringify(frame)).catch(finishErr);
    };

    const handlePullFrame = async (c: EncryptedChannel, frame: HostTransferFrame) => {
      if (settled) return;
      if (frame.type === "meta") {
        expectedSize = frame.size;
        expectedSha = frame.sha256;
        writeStream = fs.createWriteStream(partialPath, { flags: "w", mode: 0o600 });
        await new Promise<void>((res, rej) => {
          writeStream?.once("open", () => res());
          writeStream?.once("error", rej);
        });
        return;
      }
      if (frame.type === "chunk") {
        if (!writeStream) throw new Error("chunk before meta");
        if (frame.seq !== nextSeq)
          throw new Error(`out-of-order chunk ${frame.seq}, expected ${nextSeq}`);
        const bytes = Buffer.from(frame.dataB64, "base64");
        bytesWritten += bytes.byteLength;
        if (expectedSize >= 0 && bytesWritten > expectedSize)
          throw new Error("more bytes than announced");
        hash.update(bytes);
        nextSeq += 1;
        const ok = writeStream.write(bytes);
        if (!ok) await new Promise<void>((res) => writeStream?.once("drain", () => res()));
        if (nextSeq % HOST_TRANSFER_ACK_EVERY === 0)
          sendFrame(c, { type: "ack", seq: nextSeq - 1 });
        return;
      }
      if (frame.type === "done") {
        if (!writeStream) throw new Error("done before meta");
        await new Promise<void>((res, rej) => {
          writeStream?.end((err: NodeJS.ErrnoException | null | undefined) =>
            err ? rej(err) : res(),
          );
        });
        writeStream = null; // prevent cleanup() from destroying + rm'ing on success
        const digest = hash.digest("hex");
        if (bytesWritten !== expectedSize || digest !== expectedSha) {
          sendFrame(c, { type: "error", message: "verification failed on receiver" });
          throw new Error(
            `verification failed (${bytesWritten}/${expectedSize} bytes, sha256 ${digest} vs ${expectedSha})`,
          );
        }
        await fsp.rename(partialPath, localPath);
        sendFrame(c, { type: "ok", bytes: bytesWritten });
        finishOk({ bytes: bytesWritten, sha256: digest, transport: selectedTransport });
        return;
      }
      if (frame.type === "error") throw new Error(`sibling: ${frame.message}`);
    };

    const runPush = async (c: EncryptedChannel) => {
      await new Promise<void>((res) => {
        pushReady = res;
      });
      if (settled) return;
      const handle = await fsp.open(localPath, "r");
      try {
        const buf = Buffer.alloc(HOST_TRANSFER_CHUNK_BYTES);
        let seq = 0;
        let position = 0;
        for (;;) {
          if (settled) return;
          const { bytesRead } = await handle.read(buf, 0, buf.length, position);
          if (bytesRead <= 0) break;
          position += bytesRead;
          if (seq - ackedSeq > HOST_TRANSFER_WINDOW_CHUNKS) {
            await new Promise<void>((res) => {
              onAck = (acked) => {
                if (seq - acked <= HOST_TRANSFER_WINDOW_CHUNKS || settled) {
                  onAck = null;
                  res();
                }
              };
            });
            if (settled) return;
          }
          await c.send(
            JSON.stringify({
              type: "chunk",
              seq,
              dataB64: buf.subarray(0, bytesRead).toString("base64"),
            } satisfies HostTransferFrame),
          );
          touch();
          seq += 1;
        }
        sendFrame(c, { type: "done", chunks: seq });
        const bytes = await new Promise<number>((res) => {
          finalOk = res;
        });
        finishOk({ bytes, sha256: pushSha256, transport: selectedTransport });
      } finally {
        await handle.close();
      }
    };

    createClientChannel(
      transport,
      peer.daemonPubkey,
      {
        onopen: () => {
          const c = channelRef;
          if (!c || helloSent) return;
          helloSent = true;
          touch();
          const hello = {
            type: "hello",
            clientId,
            clientType: "cli",
            protocolVersion: WS_PROTOCOL_VERSION,
            wsToken,
            mode: "host-transfer",
            transfer:
              transfer.direction === "pull"
                ? { op: "get", path: transfer.remotePath }
                : { op: "put", path: transfer.remotePath, size: pushSize, sha256: pushSha256 },
          } as const;
          c.send(JSON.stringify(hello)).catch(finishErr);
          if (transfer.direction === "push") {
            runPush(c).catch(finishErr);
          }
        },
        onmessage: (data) => {
          if (typeof data !== "string") return;
          touch();
          let parsed: unknown;
          try {
            parsed = JSON.parse(data);
          } catch {
            return;
          }
          const frame = hostTransferFrameSchema.safeParse(parsed);
          if (!frame.success) return;
          const c = channelRef;
          if (!c) return;

          if (transfer.direction === "pull") {
            frameQueue = frameQueue.then(() => handlePullFrame(c, frame.data)).catch(finishErr);
            return;
          }
          // push: acks / ready / ok / error
          switch (frame.data.type) {
            case "ready":
              pushReady?.();
              break;
            case "ack":
              ackedSeq = Math.max(ackedSeq, frame.data.seq);
              onAck?.(ackedSeq);
              break;
            case "ok":
              finalOk?.(frame.data.bytes);
              break;
            case "error":
              finishErr(new Error(`sibling: ${frame.data.message}`));
              break;
            default:
              break;
          }
        },
        onclose: (code, reason) => {
          if (!settled) finishErr(new Error(`channel closed early: ${code} ${reason}`));
        },
        onerror: (err) => {
          finishErr(err);
        },
      },
      clientIdentity,
    )
      .then((opened) => {
        channelRef = opened;
        log.debug(
          { clientIdentityPubkeyB64, direction: transfer.direction },
          "host-transfer channel open",
        );
      })
      .catch(finishErr);
  });
}

async function sha256OfFile(filePath: string): Promise<string> {
  const hash = createHash("sha256");
  await new Promise<void>((resolve, reject) => {
    const rs = fs.createReadStream(filePath);
    rs.on("data", (chunk) => hash.update(chunk));
    rs.on("end", () => resolve());
    rs.on("error", reject);
  });
  return hash.digest("hex");
}
